import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import {
  IOrderErrors,
  ITypesOfErrors,
  OrderErrors,
} from 'src/app/models/Orders.model';
import { OrdersService } from 'src/app/services/orders.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * ModifyErrorComponent — sub-modale per modificare un errore già
 * registrato su un ordine (lanciato da `<app-storico-ordini-dettagli>`
 * H.2 quando l'utente clicca su un errore nella lista errori).
 *
 * Sibling mutually-exclusive di `<app-errori>` (H.3, modale di
 * aggiunta nuovo errore): entrambi sono sub-modali aperti dal modale
 * principale H.2. Stesso z-index 2100 (mai aperti insieme).
 *
 * Emette `modalClosed1` al parent dopo save success o cancel così la
 * lista errori può ricaricarsi senza full page reload.
 *
 * Phase 1.5 / v2.9.0 — TS cleaned up (Chunk H.4):
 *   • dropped kendo-dialog + 3× kendo-formfield + 3× kendo-label +
 *     kendoTextBox + 2× kendo-combobox + 2× kendo-formerror
 *     (12 kendo refs → 0 in HTML),
 *   • dropped 9 console.log/debug (incluso "cacat" 🙄, "Mofied error"
 *     typo, "Success!", "ionChange fired with value:..."),
 *   • dropped 12 dead state fields: `itemForSettings`,
 *     `oe_description_model`, `oe_description`, `oe_error_value`,
 *     `oe_error_gravity`, `er_error_type_id`, `order_error_id`,
 *     `order_error_type_description`, `numbersArray` (duplicato di
 *     `values`), `error_code`, `modify`, `opened`,
 *   • dropped 3 handler dead: `handleChange` (mai chiamato dal
 *     template), `letModify` (mai chiamato), `goBackFunction` (=
 *     duplicato di `close()`),
 *
 *   🚨 FIX BUG critico: legacy chiamava `window.location.reload()`
 *   sia in success che in error branches di `setError()`. Hard page
 *   reset full app (perde tutta la state, ri-load tutti i dati).
 *   Sostituito con `modalClosed1.emit()` — il parent (errori H.3)
 *   ricarica solo la lista errori.
 *
 *   • dropped Swal customClass `'pop-your-mom'` → rinominato a
 *     `'swal-storico-modal'` (civile),
 *   • switch a `inject()` + `takeUntilDestroyed`,
 *   • Validators.required aggiunti correttamente per evitare submit
 *     con campi vuoti (il legacy aveva `[required]="true"` solo nel
 *     template, non nella FormGroup).
 *
 * Behaviour preserved 1:1: `updateError()` endpoint, payload shape
 * `IOrderErrors`, Swal success/error feedback.
 */
@Component({
  selector: 'app-modify-error',
  templateUrl: './modify-error.component.html',
  styleUrls: ['./modify-error.component.scss'],
  standalone: false,
})


export class ModifyErrorComponent implements OnInit {

  // ── Injected services ─────────────────────────────────────────────
  private readonly ordersServ = inject(OrdersService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  public readonly closeServ = inject(OpenModalService);

  // ── Inputs / Outputs ──────────────────────────────────────────────
  @Input() order_id!: number;
  @Input() error: IOrderErrors | null = null;
  @Output() modalClosed1 = new EventEmitter<void>();

  // ── Form ──────────────────────────────────────────────────────────
  public errorForm!: FormGroup;

  // ── Data ──────────────────────────────────────────────────────────
  public errorTypes: ITypesOfErrors[] = [];
  /** Possibili valori 1-10 per la gravità errore. */
  public readonly values: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  // ── UI state ──────────────────────────────────────────────────────
  public saving = false;

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.errorForm = this.formBuilder.group({
      order_id: [this.order_id],
      oe_description: [
        this.error?.oe_description ?? '',
        [Validators.required, Validators.maxLength(500)],
      ],
      oe_error_gravity: [
        this.error?.oe_error_gravity ?? '',
        Validators.required,
      ],
      oe_error_type_id: [
        this.error?.oe_error_type_id ?? '',
        Validators.required,
      ],
      order_error_id: [this.error?.order_error_id ?? null],
    });
    this.loadErrorTypes();
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loaders.
  // ──────────────────────────────────────────────────────────────────

  private loadErrorTypes(): void {
    this.ordersServ
      .getTypesOfOrderErrors()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: ITypesOfErrors[]) => {
          this.errorTypes = data ?? [];
          this.cdr.detectChanges();
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Submit.
  // ──────────────────────────────────────────────────────────────────

  setError(): void {
    if (!this.errorForm.valid || this.saving) return;
    this.saving = true;

    const f = this.errorForm.value;
    const modifiedError = new OrderErrors({
      oe_description: f.oe_description,
      oe_error_gravity: f.oe_error_gravity,
      oe_error_type_id: f.oe_error_type_id,
      order_error_id: this.error?.order_error_id,
      order_id: this.order_id,
    });

    this.ordersServ
      .updateError(modifiedError)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          this.saving = false;
          if (data?.success) {
            // FIX BUG: legacy chiamava window.location.reload() —
            // ora emetto event al parent così solo la lista errori
            // si ricarica senza perdere la state dell'app.
            this.modalClosed1.emit();
            this.closeServ.closeModifyError();
            Swal.fire({
              title: 'Modificato!',
              text: 'Errore modificato con successo.',
              icon: 'success',
              customClass: { popup: 'swal-storico-modal' },
              timer: 1500,
              timerProgressBar: true,
            });
          } else {
            Swal.fire({
              title: 'Non modificato',
              text: 'Errore non modificato, riprova.',
              icon: 'error',
              customClass: { popup: 'swal-storico-modal' },
              timer: 1500,
              timerProgressBar: true,
            });
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.saving = false;
          Swal.fire({
            title: 'Non modificato',
            text: 'Errore di rete, riprova.',
            icon: 'error',
            customClass: { popup: 'swal-storico-modal' },
            timer: 1500,
            timerProgressBar: true,
          });
          this.cdr.detectChanges();
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Close.
  // ──────────────────────────────────────────────────────────────────

  close(): void {
    this.closeServ.closeModifyError();
  }
  
}
